import { FC, useEffect, useRef, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useLocation } from 'react-router-dom';
import { Button, IconButton, Menu, MenuItem, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { flexHelper, Icon, shortenPhrase, TRANSITION_300, useModal } from 'shared';
import { useWalletConnectorContext } from 'shared/config/wallet-connect';

type UserMenuProps = {
  address?: string;
};

const COPY_TEXT_SHOW_TIME = 2000;

const copyTextHelpers = {
  success: 'Copied!',
  error: 'Not copied!',
};

export const UserMenu: FC<UserMenuProps> = ({ address }) => {
  const popoverRef = useRef(null);
  const theme = useTheme();
  const { disconnect } = useWalletConnectorContext();
  const { pathname } = useLocation();
  const [helperText, setHelperText] = useState('');
  const handleCopyText = () => {
    setHelperText(copyTextHelpers.success);
  };
  const [isAccountOpen, onOpenAccount, onCloseAccount] = useModal(false);
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    onCloseAccount();
  }, [pathname, onCloseAccount]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setHelperText('');
    }, COPY_TEXT_SHOW_TIME);
    return () => clearTimeout(timer);
  }, [address, helperText]);
  return (
    <>
      <Button
        ref={popoverRef}
        variant="outlined"
        onClick={onOpenAccount}
        endIcon={<Icon type="arrow-down" width={10} />}
        sx={{
          width: '182px',
          borderRadius: '158px',
          svg: {
            transition: TRANSITION_300,
            transform: `rotate(${isAccountOpen ? '180deg' : '0deg'})`,
          },
        }}
      >
        {address ? shortenPhrase(address, 6, 5) : 'Connect wallet'}
      </Button>
      <Menu
        anchorEl={popoverRef.current}
        open={isAccountOpen}
        slotProps={{
          paper: {
            sx: {
              width: { xs: '100%', md: 328 },
            },
          },
        }}
        onClose={onCloseAccount}
        sx={{
          '.MuiBackdrop-root': { background: { xs: theme.themeColors.colorMenuBackdrop, md: 'transparent' } },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h3">{address ? 'Account' : 'Connect Wallet'}</Typography>
          <IconButton className="transparent" onClick={onCloseAccount}>
            <Icon type="cross" size={14} />
          </IconButton>
        </Stack>
        {address ? (
          <Stack>
            <CopyToClipboard text={address}>
              <MenuItem sx={{ ...flexHelper('space-between') }} onClick={handleCopyText}>
                <Typography variant="button">{shortenPhrase(address, 6, 5)}</Typography>
                {helperText ? (
                  <Typography variant="button" noWrap maxWidth="100%" fontWeight={500}>
                    {helperText}
                  </Typography>
                ) : (
                  <Icon type="copy" size={24} />
                )}
              </MenuItem>
            </CopyToClipboard>
            <MenuItem sx={{ ...flexHelper('space-between') }} onClick={() => disconnect()}>
              <Typography variant="button">Disconnect</Typography>
              <Icon type="exit" size={24} />
            </MenuItem>
          </Stack>
        ) : (
          <Stack>
            <MenuItem>
              <Icon type="metamask" size={42} />
              <Typography variant="button" sx={{ ml: '10px' }}>
                MetaMask
              </Typography>
            </MenuItem>
            <MenuItem>
              <Icon type="walletconnect" size={42} />
              <Typography variant="button" sx={{ ml: '10px' }}>
                Wallet connect
              </Typography>
            </MenuItem>
          </Stack>
        )}
      </Menu>
    </>
  );
};
